// Economic Calendar — iOS HIG with filter chips + iOS list
// Reference: tab segmented control, search field, country/intensity dropdowns,
// toggle row, list of events as inset-grouped list.

import { useEffect, useMemo, useState } from 'react';
import { APP_ID, type EconomicCalendarEvent } from '@funderpro/shared';
import { API_BASE } from '../../lib/auth';
import { Card, Empty, ErrorBox, FormField, Loading, SectionHeader, ToggleRow } from './UIShell';
import { CountryFlag, countryName } from '../../components/Brand';

export function EconomicCalendarPage() {
  const [allEvents, setAllEvents] = useState<EconomicCalendarEvent[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('All');
  const [impact, setImpact] = useState('All');
  const [onlyRestricted, setOnlyRestricted] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/apps/${APP_ID}/entities/EconomicCalendar`)
      .then(r => r.json())
      .then(d => { if (d?.error_type) setErr(d.message); else setAllEvents(Array.isArray(d) ? d : []); })
      .catch(e => setErr(String(e?.message || e)));
  }, []);

  const countries = useMemo(() => {
    if (!allEvents) return [] as string[];
    return ['All', ...Array.from(new Set(allEvents.map(e => e.country))).sort()].map(c => c === 'All' ? 'All' : countryName(c));
  }, [allEvents]);

  const filtered = useMemo(() => {
    if (!allEvents) return [];
    return allEvents.filter(e => {
      if (onlyRestricted && !e.restricted) return false;
      if (country !== 'All' && e.country !== country) return false;
      if (impact !== 'All' && e.impact !== impact) return false;
      if (search && !e.description.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [allEvents, search, country, impact, onlyRestricted]);

  if (err) return <div className="p-4"><ErrorBox>{err}</ErrorBox></div>;
  if (!allEvents) return <Loading label="Loading calendar…" />;

  return (
    <div className="p-4">
      <h1 className="px-1 text-[34px] font-bold leading-[1.1] tracking-tight text-tf-text-1">Economic calendar</h1>
      <p className="mt-1 px-1 text-[15px] text-tf-text-3">High-impact news may auto-liquidate new positions 2 minutes before/after.</p>

      <Card className="mt-4">
        <p className="text-[13px] leading-relaxed text-tf-text-2">
          Some news events may be restricted. New positions entered 2 minutes before and after high impact news will be auto liquidated. Holding and closing pre-existing positions through high impact news is allowed.
        </p>
      </Card>

      <SectionHeader>Filters</SectionHeader>
      <div className="space-y-3">
        <FormField label="Search" name="cal-search" value={search} onChange={setSearch} placeholder="Search events…" />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Country" value={country} onChange={setCountry} options={countries} testId="cal-country" />
          <SelectField label="Impact" value={impact} onChange={setImpact} options={['All', 'low', 'medium', 'high']} testId="cal-impact" />
        </div>
      </div>

      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Options</h2>
      <div className="overflow-hidden rounded-[16px] bg-white">
        <ToggleRow checked={onlyRestricted} onChange={setOnlyRestricted} label="Show only restricted events" id="restrict" />
      </div>

      <SectionHeader>Events ({filtered.length})</SectionHeader>
      <div className="overflow-hidden rounded-[16px] bg-white">
        {filtered.length === 0 ? (
          <div className="p-4">
            <Empty title="No events match" body="Try changing your filters or disabling the restricted-only toggle." />
          </div>
        ) : (
          filtered.slice(0, 20).map((e, i) => (
            <div key={e.id} className={['flex items-start gap-3 px-4 py-3', i === 0 ? '' : 'border-t border-tf-border-strong/55'].join(' ')}>
              <CountryFlag code={e.country} size={36} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold text-tf-text-1">{e.description}</span>
                  {e.restricted && <span className="rounded-full bg-[#FF3B30]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-tf-loss">Restricted</span>}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[12px] text-tf-text-3">
                  <span>{e.country}</span>
                  <span>·</span>
                  <span>{new Date(e.event_date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[12px] text-tf-text-3">Act</div>
                <div className="text-[13px] font-semibold tabular-nums text-tf-text-1">{e.actual || '—'}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }: { label: string; value: string; onChange: (v: string) => void; options: string[]; testId?: string }) {
  return (
    <div>
      <div className="mb-1.5 px-1 text-[12px] font-medium text-tf-text-3">{label}</div>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full appearance-none rounded-[10px] border border-tf-border-strong bg-white px-4 py-3 text-[15px] font-medium text-tf-text-1 outline-none focus:border-[#007AFF]"
        >
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <svg viewBox="0 0 24 24" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
    </div>
  );
}
